<script setup>
import { computed, ref, watch } from 'vue';

const props = defineProps({
    show: {
        type: Boolean,
        default: false
    },
    downloadUrl: {
        type: String,
        default: '/hkcm/images/osterkampagne/ph-ostern-1.png'
    }
});

const emits = defineEmits(['update:show', 'accept', 'decline']);

const dataConsent = ref(false);
const showValidation = ref(false);

watch(
    () => props.show,
    (val) => {
        if (val) {
            dataConsent.value = false;
            showValidation.value = false;
        }
    }
);

const canSubmit = computed(() => dataConsent.value);

const handleOsterDownload = () => {
    const link = document.createElement('a');
    link.href = props.downloadUrl;
    link.download = 'hkcm_design_vorlage_ostern2026.png';
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

const handleClose = () => {
    emits('update:show', false);
};

const handleDecline = () => {
    handleClose();
    emits('decline');
};

const handleAccept = () => {
    if (!canSubmit.value) {
        showValidation.value = true;

        return;
    }

    handleClose();
    handleOsterDownload();
};
</script>

<template>
    <transition name="fade">
        <div v-if="show" class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" @click.self="handleDecline">
            <div class="relative w-full max-w-2xl mx-4 rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto">
                <!-- Gold top bar -->
                <div class="h-1.5 bg-gradient-to-r from-[#93802F] via-[#E2C750] to-[#93802F]"></div>

                <div class="bg-gradient-to-b from-[#072551] via-[#051A39] to-[#020E20] text-white p-6 sm:p-8 space-y-6">
                    <!-- Header -->
                    <div class="flex items-start justify-between gap-4">
                        <div class="flex items-start gap-4">
                            <div>
                                <h2 class="text-lg sm:text-xl font-semibold tracking-tight text-white">Teilnahmebedingungen und Datenschutz</h2>
                                <p class="mt-1 text-sm text-slate-300 leading-relaxed">
                                    Bevor Sie an „Paint The Hopf" teilnehmen, benötigen wir Ihre Zustimmung zu den Teilnahmebedingungen und zur Verarbeitung Ihrer Daten.
                                </p>
                            </div>
                        </div>
                        <!-- Close button -->
                        <button
                            type="button"
                            class="shrink-0 flex items-center h-8 w-8 justify-center rounded-full border border-white/10 text-slate-300 hover:text-white hover:border-white/30 hover:bg-white/5 transition-colors"
                            aria-label="Schließen"
                            @click="handleDecline"
                        >
                            <span class="fi fi-rr-cross-small flex items-center justify-center text-base"></span>
                        </button>
                    </div>

                    <!-- Detail Links -->
                    <div>
                        <p class="text-sm text-slate-400 mb-3">Details finden Sie hier:</p>
                        <div class="flex flex-col sm:flex-row gap-3">
                            <a
                                href="/verlosung"
                                target="_blank"
                                rel="noopener noreferrer"
                                class="inline-flex items-center gap-2 rounded-full border border-[#006CFF]/70 bg-[#006CFF]/40 px-4 py-2.5 text-sm font-medium text-blue-100 hover:border-blue-400 hover:bg-[#006CFF]/60 transition-colors"
                            >
                                <i class="fi fi-rr-external-link flex items-center text-xs"></i>
                                <span>Zu den Teilnahmebedingungen</span>
                            </a>
                            <a
                                href="/verlosungdatenschutz"
                                target="_blank"
                                rel="noopener noreferrer"
                                class="inline-flex items-center gap-2 rounded-full border border-[#006CFF]/70 bg-[#006CFF]/40 px-4 py-2.5 text-sm font-medium text-blue-100 hover:border-blue-400 hover:bg-[#006CFF]/60 transition-colors"
                            >
                                <i class="fi fi-rr-external-link flex items-center text-xs"></i>
                                <span>Zu den Datenschutzhinweisen</span>
                            </a>
                        </div>
                    </div>

                    <!-- Checkbox -->
                    <div class="rounded-xl bg-white/5 border border-white/10 px-4 py-4 space-y-3">
                        <label class="flex items-start gap-3 cursor-pointer">
                            <input
                                v-model="dataConsent"
                                type="checkbox"
                                class="mt-1 h-4 w-4 rounded border-slate-500 bg-slate-900 text-[#006CFF] focus:ring-[#006CFF] focus:ring-offset-slate-900 accent-[#006CFF]"
                                @change="showValidation = false"
                            />
                            <span class="text-sm text-slate-200 leading-relaxed">
                                Ich habe die Teilnahmebedingungen und die Datenschutzhinweise gelesen und stimme ihnen zu.
                                <span class="text-[#E2C750] font-semibold">*</span>
                            </span>
                        </label>

                        <!-- Validation warning -->
                        <transition name="slide">
                            <div v-if="showValidation && !canSubmit" class="flex items-center gap-2 rounded-lg bg-amber-400/15 border border-amber-400/40 px-3 py-2">
                                <i class="fi fi-rr-triangle-warning flex items-center text-amber-400 text-sm"></i>
                                <p class="text-xs text-amber-300">Bitte bestätigen Sie alle Felder, um fortzufahren.</p>
                            </div>
                        </transition>
                    </div>

                    <!-- Action Buttons -->
                    <div class="flex flex-col sm:flex-row sm:justify-end gap-3 pt-1">
                        <button
                            type="button"
                            class="inline-flex justify-center items-center rounded-full border border-slate-600/80 px-5 py-2.5 text-sm font-medium text-slate-200 hover:bg-slate-800/80 hover:border-slate-400/80 transition-colors"
                            @click="handleDecline"
                        >
                            Abbrechen
                        </button>
                        <button
                            type="button"
                            :disabled="!canSubmit"
                            class="inline-flex justify-center items-center rounded-full px-5 py-2.5 text-sm font-semibold text-white shadow-lg transition-all"
                            :class="canSubmit ? 'opacity-100 cursor-pointer' : 'opacity-50 cursor-not-allowed'"
                            :style="canSubmit ? 'background: linear-gradient(to bottom right, #93802F, #E2C750)' : 'background: linear-gradient(to bottom right, #93802F, #E2C750)'"
                            @click="handleAccept"
                        >
                            <i class="fi fi-rr-check flex items-center mr-2"></i>
                            Einverstanden &amp; teilnehmen
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </transition>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease-out;
}
.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.slide-enter-active,
.slide-leave-active {
    transition: all 0.2s ease-out;
}
.slide-enter-from,
.slide-leave-to {
    opacity: 0;
    transform: translateY(-4px);
}
</style>
